// Copyright 2025, University of Maryland, All Rights Reserved
import * as React from "react"
import { cn } from "@/lib/utils"
import type { LucideIcon } from "lucide-react"

export interface InputWithIconsProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  startIcon?: LucideIcon
  endIcon?: LucideIcon | React.ReactNode
  startIconClassName?: string
  endIconClassName?: string
}

const InputWithIcons = React.forwardRef<HTMLInputElement, InputWithIconsProps>(
  (
    {
      className,
      type,
      startIcon: StartIcon,
      endIcon,
      startIconClassName,
      endIconClassName,
      ...props
    },
    ref
  ) => {
    const EndIconComponent =
      endIcon && typeof endIcon !== "function" ? null : (endIcon as LucideIcon)

    return (
      <div className="relative w-full">
        {StartIcon && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center pointer-events-none">
            <StartIcon
              className={cn("h-4 w-4 text-muted-foreground", startIconClassName)}
            />
          </div>
        )}
        <input
          type={type}
          className={cn(
            "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
            StartIcon && "pl-10",
            endIcon && "pr-10",
            className
          )}
          ref={ref}
          {...props}
        />
        {endIcon && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center pointer-events-none">
            {EndIconComponent ? (
              <EndIconComponent
                className={cn("h-4 w-4 text-muted-foreground", endIconClassName)}
              />
            ) : (
              <>{endIcon}</>
            )}
          </div>
        )}
      </div>
    )
  }
)

InputWithIcons.displayName = "InputWithIcons"

export { InputWithIcons }
